/**
 * Auth Dialog Component
 *
 * Nested route dialog for login and signup that overlays the home page.
 * Uses email/password authentication with OTP verification for signup.
 *
 * Routes: /auth/login and /auth/signup
 *
 * Login flow: Email + Password → signInWithPassword() → Complete
 * Signup flow: Email + Password + Name → OTP verification → Account creation
 */
import type { Route } from "./+types/auth-dialog";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Form,
  Link,
  data,
  redirect,
  useNavigate,
  useNavigation,
} from "react-router";
import { z } from "zod";

import { getClientInfo } from "~/core/lib/request-info.server";
import adminClient from "~/core/lib/supa-admin-client.server";
import makeServerClient from "~/core/lib/supa-client.server";
import { logAccessEvent } from "~/features/admin/utils/access-logs";
import { GoogleLogo } from "~/features/auth/components/logos/google";
import { KakaoLogo } from "~/features/auth/components/logos/kakao";

// Terms of Service Text (Korean)
const TERMS_OF_SERVICE_TEXT = `이용약관

제1조 목적
본 약관은 슬로우키니(이하 "회사")이 제공하는 AI 사진 생성 및 관련 제반 서비스(이하 "서비스")의 이용과 관련하여 회사와 회원 간의 권리, 의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.

제2조 정의
1. "크레딧"은 서비스 내 이미지 생성에 사용되는 디지털 이용권이다.
2. "생성 이미지"는 사용자가 입력한 텍스트 또는 업로드한 자료를 기반으로 AI 모델이 생성한 디지털 결과물이다.
3. "회원"은 서비스 이용을 위해 가입하고 이용계정을 발급받은 자를 말한다.
4. "결제"는 회원이 크레딧을 구매하기 위해 이용하는 모든 전자적 결제 행위를 의미한다.

제3조 서비스 내용
1. 회원은 크레딧을 충전하여 이미지를 생성할 수 있으며, 이미지 생성 1회당 콘텐츠별로 지정된 크레딧이 차감됩니다.
2. 생성 이미지는 AI 기술 특성상 입력 내용, 업로드 이미지의 품질, 시스템 환경 등에 따라 결과가 달라질 수 있으며, 특정 결과의 일관성 또는 재현성은 보장되지 않습니다.
3. 이미지 생성 과정에서 회원이 업로드한 파일은 처리 목적에 한해 일시적으로 사용되며, 생성 작업 완료 후 즉시 삭제됩니다.
4. 시스템 오류로 인해 이미지 생성이 정상적으로 이루어지지 않은 것이 확인된 경우, 회사는 금전 환불 대신 동일 수량의 크레딧을 재지급하는 방식으로 보상합니다.

제4조 결제 및 크레딧 제공
1. 크레딧 구매는 선불 충전 방식이며, 결제 완료 후 즉시 계정에 반영된다.
2. 카드결제 후 취소 요청 시 7영업일 이내에 결제된 카드로 취소 처리되며, 카드사 사정에 따라 취소 완료까지 3일에서 7일이 소요될 수 있습니다.
3. 부정 이용, 명의 도용, 결제 오류 등 이상거래가 확인될 경우 회사는 결제를 취소하거나 이용을 제한할 수 있다.
4. 이벤트·프로모션 등으로 지급된 무상 크레딧은 환불 및 현금 교환이 불가하다.
5. 회원이 사진을 업로드하는 경우, 회사는 이를 분석하여 회원이 선택한 컨셉·스타일·조건에 맞는 맞춤형 생성 이미지를 제공할 수 있다. 업로드된 자료는 분석 목적 범위 내에서만 사용되며, 관련 법령 및 개인정보처리방침에 따라 안전하게 처리된다.

제5조 생성 이미지의 특성
1. 생성 이미지는 대부분 안정적으로 생성되지만, 기술적 특성상 결과물의 구도·색감·세부 표현 등에 편차가 발생할 수 있다.
2. 동일한 입력 조건이라도 결과가 일관되지 않거나 변동될 수 있으며, 이는 AI 기반 생성 기술에서 일반적으로 나타나는 현상이다.
3. 일부 왜곡이나 묘사 차이는 서비스 고유 특성으로 환불 사유에 해당하지 않는다.
4. 회사는 품질 향상을 위해 모델·시스템을 지속적으로 개선한다.

제6조 회원의 의무
회원은 서비스를 이용함에 있어 다음 행위를 하여서는 아니 된다.
1. 타인의 개인정보를 도용하거나 부정하게 사용하는 행위
2. 음란물·불법 촬영물 등 관련 법령에 위반되는 이미지 생성 요청
3. 저작권·초상권 등 타인의 권리를 침해하는 콘텐츠를 업로드하거나 요청하는 행위
4. 자동화 도구·스크립트 등 비정상적인 방식으로 서비스를 이용하는 행위
5. 회사의 시스템, 네트워크, 보안 기능을 방해하거나 무단으로 접근을 시도하는 행위
6. 회원이 본 조의 의무를 위반하여 발생한 법적 분쟁·권리 침해·손해 등에 대한 책임은 회원 본인에게 있으며, 이로 인해 회사에 손해가 발생한 경우 회원은 그 손해를 배상하여야 한다.
7. 회사는 회원이 입력·업로드한 콘텐츠의 적법성을 사전 검토할 의무가 없으며, 회원이 위법한 콘텐츠를 업로드하거나 요청함으로써 발생하는 문제에 대해 책임을 부담하지 않는다.

제7조 회사의 의무
1. 회사는 관련 법령을 준수하며 안정적인 서비스 제공을 위해 최선을 다한다.
2. 회원의 개인정보를 법령과 개인정보처리방침에 따라 안전하게 관리한다.

제8조 책임 제한
1. AI 생성 이미지의 특성으로 인한 품질 편차, 이미지 일관성 부족 등은 서비스 특성에 해당하므로 회사는 이에 대해 책임을 부담하지 않는다.
2. 천재지변, 통신 장애 등 불가항력으로 인한 서비스 중단에 대해 회사는 책임을 지지 아니한다.

제9조 약관 변경
1. 회사는 약관을 변경할 수 있으며, 변경 시 최소 7일 전에 공지한다.
2. 중요한 내용 변경 시 30일 전에 공지한다.`;

// Privacy Policy Text (Korean)
const PRIVACY_POLICY_TEXT = `개인정보처리방침

제1조 수집하는 개인정보 항목
회사는 서비스 제공을 위해 다음의 최소한의 개인정보를 수집합니다.
1. 회원가입: 이메일, 비밀번호
2. 결제: 결제 승인번호, 주문번호, 결제 내역 등
3. 서비스 이용 과정: 이미지 생성 기록, 접속 로그, IP 주소, 기기 정보

제2조 개인정보의 이용 목적
1. 회원 관리 및 서비스 제공
2. 결제 승인 및 구매 내역 확인
3. 이미지 생성·분석 기능 제공 및 품질 개선
4. 부정 이용 방지 및 이상거래 탐지
5. 고객 문의 대응 및 민원 처리

제3조 개인정보의 보관 및 파기
1. 개인정보는 이용 목적 달성 후 지체 없이 파기한다.
2. 법령에 따라 아래 기간 동안 보관할 수 있다.
   - 전자상거래법: 결제 기록 5년
   - 전자금융거래법: 전자금융 거래기록 5년
   - 소비자 불만·분쟁 기록 3년

파기 방법
- 전자 파일: 복구 불가능한 방식으로 영구 삭제
- 회원 탈퇴 시, 법령상 필요한 정보를 제외한 모든 정보 즉시 파기

제4조 개인정보의 제3자 제공
회사는 원칙적으로 개인정보를 제3자에게 제공하지 않는다. 단, 법령에 따라 요구되는 경우 예외적으로 제공될 수 있다.

제5조 개인정보 처리위탁
회사는 이미지 분석 및 생성 기능 제공을 위해 외부 AI 서비스(API 제공업체)에 일부 처리 업무를 위탁할 수 있습니다. 업로드된 이미지 등 최소 정보가 API 서버로 전송될 수 있으며, 전송된 데이터는 처리 완료 후 즉시 삭제됩니다.

위탁 업체
- Google Cloud - Gemini API (이미지 분석 및 생성)
※ 위탁 업체는 서비스 개선 또는 제공업체 변경에 따라 수정될 수 있습니다.

제6조 정보주체의 권리
1. 회원은 자신의 개인정보에 대해 열람·정정·삭제를 요청할 수 있다.
2. 법령상 보관 의무가 있는 정보는 삭제 요청이 제한될 수 있다.

제7조 개인정보의 안전성 확보조치
회사는 개인정보를 보호하기 위해 다음 조치를 취한다.
- 접속 기록 관리
- 접근 권한 통제
- 암호화 및 보안 서버 운영
- 내부 관리 계획 수립 및 정기 점검`;

/**
 * Validation schemas
 */
const passwordSchema = z
  .string()
  .min(8, { message: "비밀번호는 최소 8자 이상이어야 합니다" })
  .regex(/[A-Za-z]/, { message: "비밀번호에 영문자가 포함되어야 합니다" })
  .regex(/[0-9]/, { message: "비밀번호에 숫자가 포함되어야 합니다" });

const loginSchema = z.object({
  email: z.string().email({ message: "올바른 이메일을 입력해주세요" }),
  password: z.string().min(1, { message: "비밀번호를 입력해주세요" }),
});

const signupSchema = z
  .object({
    email: z.string().email({ message: "올바른 이메일을 입력해주세요" }),
    name: z.string().min(1, { message: "이름을 입력해주세요" }),
    password: passwordSchema,
    confirmPassword: z.string(),
    tosAgreed: z
      .string()
      .transform((val) => val === "true")
      .pipe(z.literal(true, { message: "이용약관에 동의해주세요" })),
    policyAgreed: z
      .string()
      .transform((val) => val === "true")
      .pipe(z.literal(true, { message: "개인정보처리방침에 동의해주세요" })),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "비밀번호가 일치하지 않습니다",
    path: ["confirmPassword"],
  });

const verifySchema = z.object({
  email: z.string().email(),
  code: z.string().length(6, { message: "인증번호는 6자리입니다" }),
  password: z.string().min(1),
});

/**
 * Loader function - determine mode from URL
 */
export function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url);
  const mode = url.pathname.includes("signup") ? "signup" : "login";
  const redirectTo = url.searchParams.get("redirect") || "/";
  return { mode, redirectTo };
}

/**
 * Action function - handle authentication
 */
export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData();
  const intent = formData.get("intent") as string;

  const [client, headers] = makeServerClient(request);

  // Login with email/password (no OTP)
  if (intent === "login") {
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    const result = loginSchema.safeParse({ email, password });
    if (!result.success) {
      return data(
        { error: result.error.issues[0].message, step: "input" },
        { status: 400 },
      );
    }

    // Sign in with password directly using Supabase client
    const { data: signInData, error: signInError } =
      await client.auth.signInWithPassword({
        email: email.toLowerCase(),
        password,
      });

    if (signInError) {
      return data(
        { error: "이메일 또는 비밀번호가 올바르지 않습니다", step: "input" },
        { status: 400 },
      );
    }

    // Log successful login
    const { ipAddress, userAgent } = getClientInfo(request);
    await logAccessEvent({
      client: adminClient,
      action: "LOGIN",
      userId: signInData.user?.id,
      userEmail: email,
      resourceType: "auth",
      resourceId: "password",
      ipAddress,
      userAgent,
      metadata: { method: "password" },
    });

    // Get redirect URL from form data (default to home)
    const redirectTo = (formData.get("redirectTo") as string) || "/";
    const safeRedirect = redirectTo.startsWith("/") ? redirectTo : "/";

    return redirect(safeRedirect, { headers });
  }

  // Send signup OTP
  if (intent === "send-signup-otp") {
    const formObject = Object.fromEntries(formData);
    const result = signupSchema.safeParse(formObject);

    if (!result.success) {
      return data(
        { error: result.error.issues[0].message, step: "input" },
        { status: 400 },
      );
    }

    const email = formData.get("email") as string;
    const name = formData.get("name") as string;
    const password = formData.get("password") as string;

    // Send OTP via Edge Function
    const response = await fetch(
      `${process.env.SUPABASE_URL}/functions/v1/auth-otp/request`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          email,
          mode: "signup",
          signupData: {
            name,
            display_name: name,
          },
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      return data(
        {
          error: errorData.error || "인증번호 발송에 실패했습니다",
          step: "input",
        },
        { status: 400 },
      );
    }

    // Return email and password to move to verify step
    // Password is kept in client state, not stored server-side
    return data({ success: true, email, step: "verify" });
  }

  // Verify OTP and complete signup in one step
  if (intent === "verify-otp") {
    const email = formData.get("email") as string;
    const code = formData.get("code") as string;
    const password = formData.get("password") as string;

    const result = verifySchema.safeParse({ email, code, password });
    if (!result.success) {
      return data(
        { error: result.error.issues[0].message, step: "verify" },
        { status: 400 },
      );
    }

    // Step 1: Verify OTP via Edge Function
    const verifyResponse = await fetch(
      `${process.env.SUPABASE_URL}/functions/v1/auth-otp/verify`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ email, code }),
      },
    );

    if (!verifyResponse.ok) {
      const errorData = await verifyResponse.json();
      return data(
        {
          error: errorData.error || "잘못된 인증번호입니다",
          attemptsLeft: errorData.attemptsLeft,
          step: "verify",
        },
        { status: 400 },
      );
    }

    const { verification_token } = await verifyResponse.json();

    // Step 2: Complete signup with password
    const signupResponse = await fetch(
      `${process.env.SUPABASE_URL}/functions/v1/auth-otp/complete-signup`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          email,
          password,
          verification_token,
        }),
      },
    );

    if (!signupResponse.ok) {
      const errorData = await signupResponse.json();
      return data(
        { error: errorData.error || "회원가입에 실패했습니다", step: "verify" },
        { status: 400 },
      );
    }

    const { session } = await signupResponse.json();

    // Set session
    const { error } = await client.auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    });

    if (error) {
      return data({ error: error.message, step: "verify" }, { status: 400 });
    }

    // Log successful signup (using LOGIN action with signup method in metadata)
    const { ipAddress, userAgent } = getClientInfo(request);
    await logAccessEvent({
      client: adminClient,
      action: "LOGIN",
      userId: session.user?.id,
      userEmail: email,
      resourceType: "auth",
      resourceId: "password",
      ipAddress,
      userAgent,
      metadata: { method: "signup" },
    });

    // Get redirect URL from form data (default to home)
    const redirectTo = (formData.get("redirectTo") as string) || "/";
    const safeRedirect = redirectTo.startsWith("/") ? redirectTo : "/";

    return redirect(safeRedirect, { headers });
  }

  return data({ error: "Invalid intent" }, { status: 400 });
}

/**
 * Auth Dialog Component
 */
export default function AuthDialog({
  loaderData,
  actionData,
}: Route.ComponentProps) {
  const navigate = useNavigate();
  const navigation = useNavigation();
  const [mounted, setMounted] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Check if form is submitting
  const isSubmitting = navigation.state === "submitting";

  // State
  const [mode, setMode] = useState<"login" | "signup">(
    loaderData.mode as "login" | "signup",
  );
  const [step, setStep] = useState<"input" | "verify">("input");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Terms & Policy
  const [tosAgreed, setTosAgreed] = useState(false);
  const [policyAgreed, setPolicyAgreed] = useState(false);
  const [showTos, setShowTos] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [canAgree, setCanAgree] = useState(false);

  // Handle client-side mounting
  useEffect(() => {
    setMounted(true);
  }, []);

  // Handle action data from server
  useEffect(() => {
    if (actionData) {
      if ("error" in actionData && actionData.error) {
        setError(actionData.error);
      }
      if ("step" in actionData && actionData.step) {
        setStep(actionData.step as "input" | "verify");
      }
      if ("email" in actionData && actionData.email) {
        setEmail(actionData.email);
      }
    }
  }, [actionData]);

  // Handle scroll for Terms/Policy agreement
  useEffect(() => {
    if (showTos || showPolicy) {
      setCanAgree(false);
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
        // Check if content is short enough
        if (
          contentRef.current.scrollHeight <= contentRef.current.clientHeight
        ) {
          setCanAgree(true);
        }
      }
    }
  }, [showTos, showPolicy]);

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 20) {
        setCanAgree(true);
      }
    }
  };

  // Close handlers
  const handleClose = () => {
    navigate("/");
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      handleClose();
    }
  };

  // ESC key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  // Reset form when switching modes
  const handleModeSwitch = (newMode: "login" | "signup") => {
    setMode(newMode);
    setStep("input");
    setError(null);
    setPassword("");
    setConfirmPassword("");
    setCode("");
  };

  // Don't render on server
  if (!mounted) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center p-4 font-['Pretendard']"
      onClick={handleBackdropClick}
    >
      <div className="absolute inset-0 animate-[fadeIn_0.3s] bg-[#F3F3F1]/95 backdrop-blur-md"></div>

      <div
        ref={modalRef}
        className="relative flex max-h-[90vh] w-full max-w-sm animate-[fadeInUp_0.3s_ease-out] flex-col overflow-hidden rounded-[32px] border border-[#1A1A1A] bg-white shadow-none"
      >
        {/* Tab Switcher */}
        <div className="flex justify-center gap-8 pt-8 pb-4">
          <button
            onClick={() => handleModeSwitch("login")}
            className={`text-sm font-bold tracking-widest uppercase transition-all ${mode === "login" ? "border-b-2 border-[#1A1A1A] pb-1 text-[#1A1A1A]" : "text-gray-300 hover:text-gray-500"}`}
          >
            Log In{" "}
            <span className="text-[10px] font-normal opacity-70">로그인</span>
          </button>
          <button
            onClick={() => handleModeSwitch("signup")}
            className={`text-sm font-bold tracking-widest uppercase transition-all ${mode === "signup" ? "border-b-2 border-[#1A1A1A] pb-1 text-[#1A1A1A]" : "text-gray-300 hover:text-gray-500"}`}
          >
            Sign Up{" "}
            <span className="text-[10px] font-normal opacity-70">회원가입</span>
          </button>
        </div>

        {/* Terms/Policy Overlay */}
        {(showTos || showPolicy) && (
          <div className="absolute inset-0 z-50 flex flex-col rounded-[32px] bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-bold tracking-wide text-[#1A1A1A] uppercase">
                {showTos ? "Terms of Service" : "Privacy Policy"}
              </h3>
              <button
                onClick={() => {
                  setShowTos(false);
                  setShowPolicy(false);
                }}
                className="text-xs font-bold text-gray-400 uppercase hover:text-[#1A1A1A]"
              >
                Close
              </button>
            </div>

            <div
              ref={contentRef}
              onScroll={handleScroll}
              className="mb-4 flex-1 overflow-y-auto rounded-xl border border-gray-100 bg-gray-50 p-4 font-mono text-xs leading-relaxed whitespace-pre-line text-gray-600"
            >
              {showTos ? TERMS_OF_SERVICE_TEXT : PRIVACY_POLICY_TEXT}
            </div>

            <button
              onClick={() => {
                if (showTos) setTosAgreed(true);
                if (showPolicy) setPolicyAgreed(true);
                setShowTos(false);
                setShowPolicy(false);
              }}
              disabled={!canAgree}
              className={`w-full rounded-full py-4 text-xs font-bold tracking-widest text-white uppercase transition-colors ${
                canAgree
                  ? "cursor-pointer bg-[#1A1A1A] hover:bg-gray-800"
                  : "cursor-not-allowed bg-gray-300"
              }`}
            >
              {canAgree ? "Agree / 동의하기" : "스크롤을 끝까지 내려주세요"}
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="scrollbar-hide flex-1 overflow-y-auto px-8 pb-10">
          <div className="mt-2 mb-8 text-center">
            <p className="font-mono text-xs text-gray-400">
              {step === "verify"
                ? "Enter Verification Code / 인증번호 입력"
                : mode === "login"
                  ? "Enter your credentials / 로그인 정보 입력"
                  : "Enter your details below / 정보를 입력하세요"}
            </p>
          </div>

          <Form
            method="post"
            className="space-y-6"
            onSubmit={() => setError(null)}
          >
            {/* Determine intent based on mode and step */}
            <input
              type="hidden"
              name="intent"
              value={
                mode === "login"
                  ? "login"
                  : step === "input"
                    ? "send-signup-otp"
                    : "verify-otp"
              }
            />
            <input
              type="hidden"
              name="redirectTo"
              value={loaderData.redirectTo}
            />

            {/* LOGIN MODE */}
            {mode === "login" && (
              <>
                {/* Email field */}
                <div className="group relative">
                  <input
                    type="text"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email / 이메일"
                    disabled={isSubmitting}
                    className="w-full border-b border-gray-200 bg-transparent py-3 text-sm font-medium text-[#1A1A1A] placeholder-gray-300 transition-colors focus:border-[#1A1A1A] focus:outline-none disabled:opacity-50"
                  />
                </div>

                {/* Password field */}
                <div className="group relative">
                  <input
                    type="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password / 비밀번호"
                    disabled={isSubmitting}
                    className="w-full border-b border-gray-200 bg-transparent py-3 text-sm font-medium text-[#1A1A1A] placeholder-gray-300 transition-colors focus:border-[#1A1A1A] focus:outline-none disabled:opacity-50"
                  />
                </div>

                {/* Forgot password link */}
                <div className="text-right">
                  <Link
                    to="/auth/forgot-password"
                    className="text-[10px] tracking-wide text-gray-400 hover:text-[#1A1A1A]"
                  >
                    Forgot Password? / 비밀번호 찾기
                  </Link>
                </div>
              </>
            )}

            {/* SIGNUP MODE - INPUT STEP */}
            {mode === "signup" && step === "input" && (
              <>
                {/* Name field */}
                <div className="group relative">
                  <input
                    type="text"
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name / 이름"
                    disabled={isSubmitting}
                    className="w-full border-b border-gray-200 bg-transparent py-3 text-sm font-medium text-[#1A1A1A] placeholder-gray-300 transition-colors focus:border-[#1A1A1A] focus:outline-none disabled:opacity-50"
                  />
                </div>

                {/* Email field */}
                <div className="group relative">
                  <input
                    type="text"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email / 이메일"
                    disabled={isSubmitting}
                    className="w-full border-b border-gray-200 bg-transparent py-3 text-sm font-medium text-[#1A1A1A] placeholder-gray-300 transition-colors focus:border-[#1A1A1A] focus:outline-none disabled:opacity-50"
                  />
                </div>

                {/* Password field */}
                <div className="group relative">
                  <input
                    type="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password / 비밀번호 (8자 이상, 영문+숫자)"
                    disabled={isSubmitting}
                    className="w-full border-b border-gray-200 bg-transparent py-3 text-sm font-medium text-[#1A1A1A] placeholder-gray-300 transition-colors focus:border-[#1A1A1A] focus:outline-none disabled:opacity-50"
                  />
                </div>

                {/* Confirm Password field */}
                <div className="group relative">
                  <input
                    type="password"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm Password / 비밀번호 확인"
                    disabled={isSubmitting}
                    className="w-full border-b border-gray-200 bg-transparent py-3 text-sm font-medium text-[#1A1A1A] placeholder-gray-300 transition-colors focus:border-[#1A1A1A] focus:outline-none disabled:opacity-50"
                  />
                </div>

                {/* Terms & Policy */}
                <div className="space-y-3 pt-2">
                  <input
                    type="hidden"
                    name="tosAgreed"
                    value={tosAgreed ? "true" : "false"}
                  />
                  <input
                    type="hidden"
                    name="policyAgreed"
                    value={policyAgreed ? "true" : "false"}
                  />

                  <label className="group flex cursor-pointer items-center gap-3">
                    <div
                      onClick={() => setTosAgreed(!tosAgreed)}
                      className={`flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 transition-colors ${tosAgreed ? "border-[#1A1A1A] bg-[#1A1A1A]" : ""}`}
                    >
                      {tosAgreed && (
                        <div className="h-1.5 w-1.5 rounded-full bg-white"></div>
                      )}
                    </div>
                    <span className="text-[10px] tracking-wide text-gray-500 uppercase transition-colors group-hover:text-black">
                      Terms of Service / 이용약관
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTos(true);
                      }}
                      className="ml-auto border-b border-transparent text-[10px] text-gray-300 hover:border-gray-300"
                    >
                      View
                    </button>
                  </label>

                  <label className="group flex cursor-pointer items-center gap-3">
                    <div
                      onClick={() => setPolicyAgreed(!policyAgreed)}
                      className={`flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 transition-colors ${policyAgreed ? "border-[#1A1A1A] bg-[#1A1A1A]" : ""}`}
                    >
                      {policyAgreed && (
                        <div className="h-1.5 w-1.5 rounded-full bg-white"></div>
                      )}
                    </div>
                    <span className="text-[10px] tracking-wide text-gray-500 uppercase transition-colors group-hover:text-black">
                      Privacy Policy / 개인정보
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowPolicy(true);
                      }}
                      className="ml-auto border-b border-transparent text-[10px] text-gray-300 hover:border-gray-300"
                    >
                      View
                    </button>
                  </label>
                </div>
              </>
            )}

            {/* SIGNUP MODE - VERIFY STEP */}
            {mode === "signup" && step === "verify" && (
              <>
                {/* Hidden fields */}
                <input type="hidden" name="email" value={email} />
                <input type="hidden" name="password" value={password} />

                {/* Verification Code Input */}
                <div className="flex justify-center py-4">
                  <input
                    type="text"
                    name="code"
                    maxLength={6}
                    value={code}
                    onChange={(e) =>
                      setCode(e.target.value.replace(/[^0-9]/g, ""))
                    }
                    disabled={isSubmitting}
                    className="w-full rounded-2xl border-none bg-gray-50 py-4 text-center text-4xl font-black tracking-[0.5em] text-[#1A1A1A] focus:outline-none disabled:opacity-50"
                    placeholder="000000"
                    autoFocus
                  />
                </div>

                <p className="text-center text-xs text-gray-400">
                  {email}로 전송된 6자리 인증번호를 입력하세요
                </p>
              </>
            )}

            {/* Error Message */}
            {error && (
              <p className="mt-2 text-center text-[10px] font-bold text-red-500 uppercase">
                {error}
              </p>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-[#1A1A1A] py-4 text-xs font-bold tracking-widest text-white uppercase shadow-none transition-all hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting && (
                <svg
                  className="h-4 w-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {isSubmitting
                ? "Processing..."
                : mode === "login"
                  ? "Log In / 로그인"
                  : step === "input"
                    ? "Continue / 계속"
                    : "Verify / 인증"}
            </button>

            {/* Social Login (login mode or signup input step) */}
            {(mode === "login" || (mode === "signup" && step === "input")) && (
              <>
                <div className="my-2 flex items-center gap-4">
                  <div className="h-px flex-1 bg-gray-100"></div>
                  <span className="text-[10px] font-bold tracking-widest text-gray-300 uppercase">
                    Or
                  </span>
                  <div className="h-px flex-1 bg-gray-100"></div>
                </div>

                <div
                  className={`flex flex-col gap-3 ${isSubmitting ? "pointer-events-none opacity-50" : ""}`}
                >
                  {/* Kakao Button */}
                  <Link
                    to="/auth/social/start/kakao"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FEE500] py-3.5 text-xs font-bold tracking-widest text-[#191919] uppercase transition-opacity hover:opacity-90"
                  >
                    <KakaoLogo className="h-4 w-4" />
                    Kakao Login
                  </Link>

                  {/* Google Button */}
                  <Link
                    to="/auth/social/start/google"
                    className="flex w-full items-center justify-center gap-2 rounded-full border border-gray-200 bg-white py-3.5 text-xs font-bold tracking-widest text-gray-700 uppercase transition-all hover:border-gray-300 hover:bg-gray-50"
                  >
                    <GoogleLogo className="h-4 w-4" />
                    Google Login
                  </Link>
                </div>
              </>
            )}

            {/* Back to Input (verify step) */}
            {step === "verify" && (
              <button
                type="button"
                onClick={() => {
                  setStep("input");
                  setCode("");
                  setError(null);
                }}
                className="mt-4 w-full text-center text-[10px] tracking-widest text-gray-400 uppercase hover:text-[#1A1A1A]"
              >
                Cancel / 취소
              </button>
            )}
          </Form>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
